/**
 * Safe wrappers for localStorage and sessionStorage.
 * All reads/writes are wrapped in try/catch so that blocked, full,
 * or corrupted storage never crashes the app.
 */

// ── localStorage ──

export const safeGetItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.warn('[safeStorage] getItem failed for', key, e);
    return null;
  }
};

export const safeSetItem = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    console.warn('[safeStorage] setItem failed for', key, e);
  }
};

export const safeRemoveItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.warn('[safeStorage] removeItem failed for', key, e);
  }
};

export const safeGetJSON = <T = unknown>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch (e) {
    console.warn('[safeStorage] getJSON failed for', key, e);
    // Remove corrupted data
    try { localStorage.removeItem(key); } catch { /* ignore */ }
    return null;
  }
};

export const safeKey = (index: number): string | null => {
  try {
    return localStorage.key(index);
  } catch (e) {
    console.warn('[safeStorage] key() failed', e);
    return null;
  }
};

export const safeLength = (): number => {
  try {
    return localStorage.length;
  } catch (e) {
    console.warn('[safeStorage] length failed', e);
    return 0;
  }
};

// ── sessionStorage ──

export const safeSessionGetItem = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    console.warn('[safeStorage] session getItem failed for', key, e);
    return null;
  }
};

export const safeSessionSetItem = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {
    console.warn('[safeStorage] session setItem failed for', key, e);
  }
};

export const safeSessionRemoveItem = (key: string): void => {
  try {
    sessionStorage.removeItem(key);
  } catch (e) {
    console.warn('[safeStorage] session removeItem failed for', key, e);
  }
};
